import {
  Component,
  OnInit,
  Input,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseFormFieldComponent, baseProviderComponent } from '../base-form-field.component';
import { DateUtils } from '@shared/core';
import { FormFieldDateModeType, IFormFieldTimePickerConfig, TimePickerValue } from '../../models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UIModalService } from '../../../modal';
import { UITimepickerModalComponent } from './timepicker-modal/timepicker-modal.component';
import { UIIcon } from '../../../icon';
import { LocalizationPipe } from '@shared/modules/localization';

export type TimeFormatType = 'HH:mm' | 'HH:mm:ss';

@Component({
  selector: 'ui-form-timepicker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, UIIcon, LocalizationPipe],
  templateUrl: './form-timepicker.component.html',
  styleUrls: ['./form-timepicker.component.scss'],
  providers: baseProviderComponent(UIFormTimepicker),
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UIFormTimepicker
  extends BaseFormFieldComponent<string | Date | null>
  implements OnInit
{
  hourValue?: number;
  minuteValue?: number;

  timeControl = new FormControl();

  @Input() override value: string | Date | null = '';
  @Input() override set config(configValue: IFormFieldTimePickerConfig) {
    this.label = configValue.label;
    this.placeholder = configValue.placeholder ?? '';
    this.hideLabel = configValue.hideLabel ?? false;
    this.required = !!configValue.required;
    this.format = configValue.format ?? 'HH:mm';
    this.loading = configValue.loading ?? false;
    this.isDurationTime = configValue.isDurationTime ?? false;
  }

  time = new Date();
  @Input() mode: FormFieldDateModeType = 'date';
  @Input() showTime?: boolean;
  @Input() format: TimeFormatType = 'HH:mm';
  @Input() maxDateOrCurrent?: Date | boolean;
  @Input() minDateOrCurrent?: Date | boolean;
  @Input() isDurationTime = false;

  get displayValue(): string {
    if (this.value) {
      const date =
        typeof this.value === 'string'
          ? DateUtils.isValidDate(this.value)
            ? new Date(this.value)
            : DateUtils.convertToDateTime(this.value)
          : this.value;

      if (date instanceof Date && !isNaN(date.getTime())) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      }
    }
    return '';
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(UIModalService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.timeControl.setValidators(this.control?.validator ?? []);
    const timeValue = this.control?.value;

    if (timeValue) {
      const date = DateUtils.isValidDate(timeValue)
        ? new Date(timeValue)
        : DateUtils.convertToDateTime(timeValue);
      this.timeControl.setValue(date);
      this.initSelectedValues(date);
      this.value = date.toISOString();
    }

    // Subscribe to control value changes
    this.control?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value) {
        const date = DateUtils.isValidDate(value)
          ? new Date(value)
          : DateUtils.convertToDateTime(value);
        this.initSelectedValues(date);
        this.value = value;
      } else {
        this.hourValue = undefined;
        this.minuteValue = undefined;
        this.value = null;
      }
    });
  }

  private initSelectedValues(date: Date): void {
    this.hourValue = date.getHours();
    this.minuteValue = date.getMinutes();
  }

  onOpenModal(): void {
    if (this.disabled) return;

    if (this.hourValue == null || this.minuteValue == null) {
      if (this.isDurationTime) {
        this.hourValue = 0;
        this.minuteValue = 0;
      } else {
        const now = new Date();
        this.hourValue = now.getHours();
        this.minuteValue = now.getMinutes();
      }
    }

    const modalRef = this.modalService.create(
      UITimepickerModalComponent,
      {
        hour: this.hourValue,
        minute: this.minuteValue,
      }
    );

    modalRef.subscribe((result) => {
      const timeValue = result as TimePickerValue | null;
      if (timeValue) {
        this.hourValue = timeValue.hour;
        this.minuteValue = timeValue.minute;

        // Create date with selected time
        const date = new Date();
        date.setHours(timeValue.hour);
        date.setMinutes(timeValue.minute);
        date.setSeconds(0);
        date.setMilliseconds(0);

        // Update value
        this.onDateChanged(date);
        this.cdr.markForCheck();
        this.timeControl.setValue(date);
      }
    });
  }

  onDateChanged(value: Date | null): void {
    this.value = value?.toISOString() ?? null;
    this.onChange(this.value);
    this.control?.setValue(this.value);
    this.control?.markAsDirty();
    this.control?.markAsTouched();
    this.updateControlValidity();
  }

  onDelete(event: MouseEvent): void {
    event.stopPropagation();
    this.control?.setValue(null);
    this.hourValue = undefined;
    this.minuteValue = undefined;
    this.value = null;
    this.onChange(null);
  }
}
